/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';
import { useLocation } from 'js/lib/useRouter';

import { Button, Dialog } from '@coursera/cds-core';
import { TranslationIcon } from '@coursera/cds-icons';

import TranslationMenuItem from 'bundles/composable-page-header/components/translation/TranslationMenuItem';
import TranslationMenuWithGrouping from 'bundles/composable-page-header/components/translation/TranslationMenuWithGrouping';
import TranslationFeedbackDialog from 'bundles/page-header/components/translation/TranslationFeedbackDialog';
import { useCourseLanguageQuery } from 'bundles/page-header/components/translation/useCourseLanguageQuery';

import _t from 'i18n!nls/composable-page-header';

import type { TranslationFeedback_TrackingInfo as TranslationFeedbackTrackingInfo } from '../../../../__generated__/graphql-types';

const COURSE_SLUG_REGEX = /\/learn\/([^/?]*)/;
const ITEM_ID_REGEX = /\/learn\/[^/]*\/[^/]*\/([^/?]*)/;
const SKILLSET_SLUG = /\/skillsets\/([^/?]*)/;
const SKILL_ID = /\/skills\/([^/?]*)/;

const useTrackingInfo = (courseId?: string, enterpriseOrgId?: string): TranslationFeedbackTrackingInfo | undefined => {
  const location = useLocation();

  if (courseId) {
    const courseSlug = location.pathname.match(COURSE_SLUG_REGEX)?.[1] ?? '';
    const itemId = location.pathname.match(ITEM_ID_REGEX)?.[1];
    return {
      inCourseTrackingInfo: {
        courseId,
        courseSlug,
        itemId,
      },
    };
  } else if (enterpriseOrgId) {
    const skillSet = location.pathname.match(SKILLSET_SLUG)?.[1];
    const skills = location.pathname.match(SKILL_ID)?.[1];
    return {
      enterpriseTrackingInfo: {
        enterpriseOrgId,
        skillSet,
        skills,
      },
    };
  }

  return undefined;
};

const styles = {
  languageGrid: css`
    column-count: 3;
    column-gap: var(--cds-spacing-200);
  `,
};

type Props = {
  open: boolean;
  onClose: () => void;
  courseId?: string;
  enterpriseOrgId?: string;
};

const TranslationMenuModal = ({ open, onClose, courseId, enterpriseOrgId }: Props) => {
  const trackingInfo = useTrackingInfo(courseId, enterpriseOrgId);
  const { loading, languages, courseLanguages } = useCourseLanguageQuery(courseId);

  const [feedbackDialogOpened, setFeedbackDialogOpened] = useState(false);

  const locale = getUserLanguageCode();

  const showGrouping = !!courseId && !!courseLanguages.originalLanguage;

  if (loading || !languages || !locale) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="translation-menu-modal-title"
        variant="standard"
        width="large"
      >
        <Dialog.HeadingGroup id="translation-menu-modal-title">{_t('Choose a language')}</Dialog.HeadingGroup>
        <Dialog.Content>
          {showGrouping ? (
            <TranslationMenuWithGrouping
              languages={languages}
              currentLanguage={locale}
              courseLanguages={courseLanguages}
            />
          ) : (
            <div role="listbox" css={styles.languageGrid}>
              {languages.map((language) => (
                <TranslationMenuItem key={language.key} language={language} currentLanguage={locale} />
              ))}
            </div>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            variant="ghost"
            size="medium"
            onClick={() => {
              setFeedbackDialogOpened(true);
            }}
            icon={<TranslationIcon size="small" />}
            iconPosition="before"
          >
            {_t('Add translation feedback')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <TranslationFeedbackDialog
        open={feedbackDialogOpened}
        onClose={() => setFeedbackDialogOpened(false)}
        trackingInfo={trackingInfo}
      />
    </>
  );
};

export default TranslationMenuModal;
